import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import range from 'lodash/range';
import sortBy from 'lodash/sortBy';
import { NavMenuPrimitives } from '@uhg-abyss/web/ui/NavMenu';
import { styled } from '@uhg-abyss/web/tools/styled';
import { usePageCollapsingHeader } from '@src/state/pageCollapsingHeader';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { useStateRouterContext } from '@uhg-abyss/web/ui/StateRouter';
import { NavMenuLinkNew } from './NavMenuLinkNew';
import { defaultEmptyFunction } from './HeaderSections/config';

const StyledNavMenuPrimitivesColumns = styled(NavMenuPrimitives.Columns, {
  // eslint-disable-next-line consistent-return
  dynamic: ({ isPageCollapsingHeaderCssStyle }) => {
    if (isPageCollapsingHeaderCssStyle) {
      return {
        '&.abyss-nav-menu-columns-root': {
          display: 'flex',
          margin: '0px',
          width: '100%',
          listStyle: 'none',
          '@media (min-width: 984px)': {
            flexDirection: 'column !important',
            padding: '$sm $lg !important',
          },
        },
      };
    }
  },
});

const StyledNavMenuPrimitivesColumn = styled(NavMenuPrimitives.Column, {
  // eslint-disable-next-line consistent-return
  dynamic: ({ isPageCollapsingHeaderCssStyle }) => {
    if (isPageCollapsingHeaderCssStyle) {
      return {
        '&.abyss-nav-menu-column-root': {
          display: 'flex',
          margin: '0px',
          width: '100%',
          listStyle: 'none',
          '@media (min-width: 984px)': {
            borderLeft: '0px !important',
            flexDirection: 'column !important',
            padding: '0px !important',
          },
        },
      };
    }
  },
});

export const NavDropDownContent = ({ dropdownKey }) => {
  const { getHeaderContents } = usePPDLagoon();
  const dropdownContents = getHeaderContents('navMenu', dropdownKey);
  const [isPageCollapsingHeaderStyle, setIsPageCollapsingHeaderStyle] =
    useState(false);

  const { showPageCollapsingHeader } = usePageCollapsingHeader.state();

  useEffect(() => {
    if (showPageCollapsingHeader) {
      setIsPageCollapsingHeaderStyle(true);
    } else {
      setIsPageCollapsingHeaderStyle(false);
    }
  }, [showPageCollapsingHeader]);

  const arrayOfSections = sortBy(
    dropdownContents.filter((content) => {
      return content.type === 'section';
    }),
    'order'
  );
  const filterBySection = (sectionId) => {
    return sortBy(
      dropdownContents.filter((content) => {
        return content.section?.toUpperCase() === sectionId;
      }),
      'order'
    );
  };

  const filterSectionByColumn = (sectionId, columnNumber) => {
    return sortBy(
      dropdownContents.filter((content) => {
        return (
          content.section?.toUpperCase() === sectionId &&
          content.column === (columnNumber + 1).toString()
        );
      }),
      'order'
    );
  };

  const { methods } = useStateRouterContext() || {
    methods: { goBack: defaultEmptyFunction },
  };

  useEffect(() => {
    return () => {
      methods.goBack();
    };
  }, []);

  return (
    <StyledNavMenuPrimitivesColumns
      isPageCollapsingHeaderCssStyle={isPageCollapsingHeaderStyle}
    >
      <For each='section' of={arrayOfSections}>
        <Choose>
          <When condition={section.numberOfColumns}>
            <For each='column' of={range(section.numberOfColumns || 1)}>
              <StyledNavMenuPrimitivesColumn
                title={column === 0 ? section.name : null}
                isPageCollapsingHeaderCssStyle={isPageCollapsingHeaderStyle}
                columnCount={section.numberOfColumns}
              >
                <For
                  each='content'
                  of={filterSectionByColumn(section.key, column)}
                >
                  <NavMenuLinkNew {...content} />
                </For>
              </StyledNavMenuPrimitivesColumn>
            </For>
          </When>
          <Otherwise>
            <StyledNavMenuPrimitivesColumn
              isPageCollapsingHeaderCssStyle={isPageCollapsingHeaderStyle}
              title={section.name}
            >
              <For each='content' of={filterBySection(section.key)}>
                <NavMenuLinkNew {...content} />
              </For>
            </StyledNavMenuPrimitivesColumn>
          </Otherwise>
        </Choose>
      </For>
    </StyledNavMenuPrimitivesColumns>
  );
};

NavDropDownContent.propTypes = {
  dropdownKey: PropTypes.string.isRequired,
};
